import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Upload,
  Download,
  Users,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/simple-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useSupabase } from "@/hooks/useSupabase";
import { Athlete } from "@shared/types";
import BackupRestore from "@/components/BackupRestore";

export default function Athletes() {
  const { store } = useSupabase();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [filteredAthletes, setFilteredAthletes] = useState<Athlete[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState<string>("all");
  const [timeSlotFilter, setTimeSlotFilter] = useState<string>("all");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // New athlete form
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAthlete, setNewAthlete] = useState({
    name: "",
    gender: "",
    timeSlot: "",
  });

  // Edit athlete
  const [editingAthlete, setEditingAthlete] = useState<Athlete | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Bulk operations
  const [isImporting, setIsImporting] = useState(false);
  const [isDuplicateCheckOpen, setIsDuplicateCheckOpen] = useState(false);

  useEffect(() => {
    loadAthletes();
  }, [store]);

  useEffect(() => {
    filterAthletes();
  }, [athletes, searchTerm, genderFilter, timeSlotFilter]);

  const loadAthletes = async () => {
    setIsLoading(true);
    try {
      const athleteList = await store.getAthletes();
      setAthletes(athleteList);
      setMessage("");
    } catch (error) {
      console.error("Error loading athletes:", error);
      setMessage("❌ Erro ao carregar atletas.");
    } finally {
      setIsLoading(false);
    }
  };

  const filterAthletes = () => {
    let filtered = athletes;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter((athlete) =>
        athlete.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filter by gender
    if (genderFilter !== "all") {
      filtered = filtered.filter((athlete) => athlete.gender === genderFilter);
    }

    // Filter by time slot
    if (timeSlotFilter !== "all") {
      filtered = filtered.filter(
        (athlete) => athlete.timeSlot === timeSlotFilter,
      );
    }

    // Sort alphabetically
    filtered.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

    setFilteredAthletes(filtered);
  };

  const handleAddAthlete = async () => {
    if (!newAthlete.name.trim() || !newAthlete.gender || !newAthlete.timeSlot) {
      setMessage("❌ Por favor, preencha todos os campos.");
      return;
    }

    try {
      const addedAthlete = await store.addAthlete({
        name: newAthlete.name.trim(),
        gender: newAthlete.gender as "Masculino" | "Feminino",
        timeSlot: newAthlete.timeSlot,
      });

      if (addedAthlete) {
        await loadAthletes();
        setNewAthlete({ name: "", gender: "", timeSlot: "" });
        setIsDialogOpen(false);
        setMessage(`✅ Atleta ${addedAthlete.name} cadastrado com sucesso!`);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("❌ Erro ao cadastrar atleta.");
      }
    } catch (error) {
      console.error("Error adding athlete:", error);
      setMessage("❌ Erro ao cadastrar atleta.");
    }
  };

  const handleEditAthlete = async () => {
    if (!editingAthlete || !editingAthlete.name.trim()) {
      setMessage("❌ Nome do atleta é obrigatório.");
      return;
    }

    try {
      const success = await store.updateAthlete(editingAthlete);
      if (success) {
        await loadAthletes();
        setEditingAthlete(null);
        setIsEditDialogOpen(false);
        setMessage(`✅ Atleta ${editingAthlete.name} atualizado com sucesso!`);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("❌ Erro ao atualizar atleta.");
      }
    } catch (error) {
      console.error("Error updating athlete:", error);
      setMessage("❌ Erro ao atualizar atleta.");
    }
  };

  const handleDeleteAthlete = async (athlete: Athlete) => {
    try {
      const success = await store.removeAthlete(athlete.id);
      if (success) {
        await loadAthletes();
        setMessage(`✅ Atleta ${athlete.name} removido com sucesso!`);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("❌ Erro ao remover atleta.");
      }
    } catch (error) {
      console.error("Error deleting athlete:", error);
      setMessage("❌ Erro ao remover atleta.");
    }
  };

  const handleBulkImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split("\n").filter((line) => line.trim());

        let imported = 0;
        let errors = 0;

        for (const line of lines) {
          const [name, gender, timeSlot] = line.split(",").map((s) => s.trim());

          if (name && gender && timeSlot) {
            try {
              const addedAthlete = await store.addAthlete({
                name,
                gender: gender as "Masculino" | "Feminino",
                timeSlot,
              });
              if (addedAthlete) imported++;
              else errors++;
            } catch {
              errors++;
            }
          } else {
            errors++;
          }
        }

        await loadAthletes();
        setMessage(
          `✅ Importação concluída: ${imported} atletas importados, ${errors} erros.`,
        );
        setTimeout(() => setMessage(""), 5000);
      } catch (error) {
        console.error("Error importing athletes:", error);
        setMessage("❌ Erro ao importar atletas.");
      } finally {
        setIsImporting(false);
        // Clear the file input
        event.target.value = "";
      }
    };

    reader.readAsText(file);
  };

  const handleExportAthletes = () => {
    const csvContent = athletes
      .map((athlete) => `${athlete.name},${athlete.gender},${athlete.timeSlot}`)
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "atletas.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    setMessage("✅ Lista de atletas exportada com sucesso!");
    setTimeout(() => setMessage(""), 3000);
  };

  const handleRemoveDuplicates = async () => {
    try {
      if ("removeDuplicateAthletes" in store) {
        const result = await (store as any).removeDuplicateAthletes();
        await loadAthletes();
        setMessage(
          `✅ Duplicatas removidas: ${result.removed} removidos, ${result.kept} mantidos.`,
        );
        setTimeout(() => setMessage(""), 5000);
      } else {
        setMessage("❌ Função de remoção de duplicatas não disponível.");
      }
    } catch (error) {
      console.error("Error removing duplicates:", error);
      setMessage("❌ Erro ao remover duplicatas.");
    } finally {
      setIsDuplicateCheckOpen(false);
    }
  };

  const getAthleteStats = () => {
    const total = athletes.length;
    const male = athletes.filter((a) => a.gender === "Masculino").length;
    const female = athletes.filter((a) => a.gender === "Feminino").length;
    const bateria1100 = athletes.filter((a) => a.timeSlot === "11:00").length;
    const bateria1430 = athletes.filter((a) => a.timeSlot === "14:30").length;

    return { total, male, female, bateria1100, bateria1430 };
  };

  const stats = getAthleteStats();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Cadastro de Atletas</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">
                {stats.total}
              </div>
              <div className="text-sm text-gray-600">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {stats.male}
              </div>
              <div className="text-sm text-gray-600">Masculino</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-pink-600">
                {stats.female}
              </div>
              <div className="text-sm text-gray-600">Feminino</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.bateria1100}
              </div>
              <div className="text-sm text-gray-600">Bateria 11:00</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">
                {stats.bateria1430}
              </div>
              <div className="text-sm text-gray-600">Bateria 14:30</div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Novo Atleta
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cadastrar Novo Atleta</DialogTitle>
                <DialogDescription>
                  Preencha as informações do atleta
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={newAthlete.name}
                    onChange={(e) =>
                      setNewAthlete({ ...newAthlete, name: e.target.value })
                    }
                    placeholder="Nome completo do atleta"
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gênero</Label>
                  <Select
                    value={newAthlete.gender}
                    onValueChange={(value) =>
                      setNewAthlete({ ...newAthlete, gender: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o gênero" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timeSlot">Bateria</Label>
                  <Select
                    value={newAthlete.timeSlot}
                    onValueChange={(value) =>
                      setNewAthlete({ ...newAthlete, timeSlot: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a bateria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11:00">Bateria 11:00</SelectItem>
                      <SelectItem value="14:30">Bateria 14:30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleAddAthlete}>Cadastrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={handleExportAthletes}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>

          <div>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleBulkImport}
              style={{ display: "none" }}
              id="csv-import"
              disabled={isImporting}
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById("csv-import")?.click()}
              disabled={isImporting}
            >
              <Upload className="w-4 h-4 mr-2" />
              {isImporting ? "Importando..." : "Importar CSV"}
            </Button>
          </div>

          <AlertDialog
            open={isDuplicateCheckOpen}
            onOpenChange={setIsDuplicateCheckOpen}
          >
            <AlertDialogTrigger asChild>
              <Button variant="outline">
                <Users className="w-4 h-4 mr-2" />
                Remover Duplicatas
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remover Atletas Duplicados</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação irá procurar por atletas com nomes idênticos e
                  remover as duplicatas, mantendo apenas o registro mais antigo.
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleRemoveDuplicates}>
                  Remover Duplicatas
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="search">Buscar por nome</Label>
                <Input
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Digite o nome do atleta..."
                />
              </div>
              <div>
                <Label htmlFor="genderFilter">Gênero</Label>
                <Select value={genderFilter} onValueChange={setGenderFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="Masculino">Masculino</SelectItem>
                    <SelectItem value="Feminino">Feminino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="timeSlotFilter">Bateria</Label>
                <Select
                  value={timeSlotFilter}
                  onValueChange={setTimeSlotFilter}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="11:00">Bateria 11:00</SelectItem>
                    <SelectItem value="14:30">Bateria 14:30</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages */}
        {message && (
          <Alert className="mb-6">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {/* Athletes Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Lista de Atletas ({filteredAthletes.length}
              {filteredAthletes.length !== athletes.length &&
                ` de ${athletes.length}`}
              )
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Carregando atletas...</p>
              </div>
            ) : filteredAthletes.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {athletes.length === 0
                    ? "Nenhum atleta cadastrado ainda."
                    : "Nenhum atleta encontrado com os filtros aplicados."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Gênero</TableHead>
                      <TableHead>Bateria</TableHead>
                      <TableHead>Cadastrado em</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAthletes.map((athlete) => (
                      <TableRow key={athlete.id}>
                        <TableCell className="font-medium">
                          {athlete.name}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              athlete.gender === "Masculino"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {athlete.gender}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              athlete.timeSlot === "11:00"
                                ? "outline"
                                : "default"
                            }
                          >
                            Bateria {athlete.timeSlot}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(athlete.createdAt).toLocaleDateString(
                            "pt-BR",
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingAthlete(athlete);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Confirmar Exclusão
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o atleta{" "}
                                  <strong>{athlete.name}</strong>? Esta ação não
                                  pode ser desfeita e todas as tentativas deste
                                  atleta também serão perdidas.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteAthlete(athlete)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Atleta</DialogTitle>
              <DialogDescription>
                Atualize as informações do atleta
              </DialogDescription>
            </DialogHeader>
            {editingAthlete && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="editName">Nome</Label>
                  <Input
                    id="editName"
                    value={editingAthlete.name}
                    onChange={(e) =>
                      setEditingAthlete({
                        ...editingAthlete,
                        name: e.target.value,
                      })
                    }
                    placeholder="Nome completo do atleta"
                  />
                </div>
                <div>
                  <Label htmlFor="editGender">Gênero</Label>
                  <Select
                    value={editingAthlete.gender}
                    onValueChange={(value) =>
                      setEditingAthlete({
                        ...editingAthlete,
                        gender: value as "Masculino" | "Feminino",
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="editTimeSlot">Bateria</Label>
                  <Select
                    value={editingAthlete.timeSlot}
                    onValueChange={(value) =>
                      setEditingAthlete({ ...editingAthlete, timeSlot: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11:00">Bateria 11:00</SelectItem>
                      <SelectItem value="14:30">Bateria 14:30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button onClick={handleEditAthlete}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Backup Section */}
        <div className="mt-12">
          <BackupRestore />
        </div>
      </main>
    </div>
  );
}
